import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toMessages } from "@/lib/form-errors"

/**
 * OKS-932 — the repeatable variant list on the manual product form.
 *
 * ## Rows are client-side until save (UX §2.2)
 *
 * No per-row request: adding, editing and removing rows all mutate local
 * state, and the whole set is submitted with the product. A variant the brand
 * removes is simply absent from the payload, and `Products::ManualWriter`
 * destroys the row it no longer sees. A half-finished edit therefore leaves
 * no orphan rows behind if the brand navigates away.
 *
 * ## ⚠️ Every row must emit EXACTLY the same field names, always
 *
 * Fields are `product[variants][][…]`, and Rails builds the array by
 * accumulating keys into the current hash and starting a new one **when it
 * sees a key that is already present**. Two consequences, both of which
 * silently scramble the payload rather than erroring:
 *
 *   1. A field emitted only on SOME rows (the obvious idea: skip `id` for a
 *      row the brand just added) gets absorbed into the PREVIOUS row's hash.
 *      A new row followed by an existing one would hand the writer one row
 *      carrying the next row's id. So `id` is always emitted, empty for new
 *      rows, and the writer treats blank as "no id".
 *
 *   2. A field emitted TWICE within one row splits that row in half. That
 *      rules out the usual hidden-"0"-plus-checkbox idiom for `available`:
 *      the pair would become two malformed rows. Instead the checkbox is a
 *      pure UI control with NO `name`, and a single controlled hidden input
 *      carries the value.
 *
 * Row ORDER is the payload order, so the writer stamps `position` from the
 * array index — the brand's ordering is the stored ordering, with no drag
 * handles and no position field to keep in sync.
 *
 * ## State is a draft shape, not the wire shape
 *
 * `price` is held as the STRING the brand is typing, never as cents. A
 * controlled numeric field cannot represent the intermediate states of typing
 * "24,90" (`"24,"` is not a number), and `defaultValue` would go stale the
 * moment a row above is removed and React reuses the element. Parsing happens
 * once, server-side, where the comma/point decision belongs anyway.
 *
 * ## Accessibility (UX §6)
 *
 * The list is a `<fieldset>` with a `<legend>`; each row is a nested
 * `<fieldset>` whose legend names the row index, so a screen reader announces
 * "Variant 2, Name" rather than four unlabelled inputs. The remove button's
 * accessible name names its row for the same reason.
 */
export interface VariantDraft {
  /** Server id, or `null` for a row the brand just added. */
  id: number | null
  title: string
  sku: string
  /** Raw as typed — "24,90" / "24.90" / "". Parsed server-side. */
  price: string
  available: boolean
}

export default function VariantRows({
  variants,
  onChange,
  limit,
  currency,
  errors,
}: {
  variants: VariantDraft[]
  onChange: (next: VariantDraft[]) => void
  limit: number
  currency: string
  /**
   * ⚠️ Inertia error VALUES are `string | string[]`, not always an array —
   * `inertia_rails` flattens a single message to a bare string, so indexing
   * `errors.x[0]` renders one CHARACTER. `toMessages` is the house
   * normaliser; never hand-roll it.
   */
  errors: Record<string, string | string[] | undefined>
}) {
  const { t } = useTranslation()
  const atCap = variants.length >= limit

  const update = (index: number, patch: Partial<VariantDraft>) => {
    onChange(
      variants.map((variant, i) =>
        i === index ? { ...variant, ...patch } : variant,
      ),
    )
  }

  const remove = (index: number) => {
    onChange(variants.filter((_, i) => i !== index))
    // Focus moves to the add control: the removed row's own button is gone,
    // and leaving focus on the body drops a keyboard user back to the top.
    requestAnimationFrame(() => {
      document.getElementById("variant-add")?.focus()
    })
  }

  const add = () => {
    const nextIndex = variants.length
    onChange([
      ...variants,
      { id: null, title: "", sku: "", price: "", available: true },
    ])
    requestAnimationFrame(() => {
      document.getElementById(`variant-title-${nextIndex}`)?.focus()
    })
  }

  return (
    <fieldset className="space-y-3" data-testid="brand-product-variants">
      <legend className="text-sm font-medium">
        {t("settings.brand.products.form.variants_legend")}
      </legend>

      {variants.map((variant, index) => (
        <fieldset
          // Existing rows key on their server id; a row the brand just added
          // has none, so it keys on position. Rows are only appended or
          // removed, never reordered, so both are stable for the list's life.
          key={variant.id ?? `new-${index}`}
          className="grid gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_120px_auto] sm:items-end"
          data-testid="brand-product-variant-row"
        >
          <legend className="sr-only">
            {t("settings.brand.products.form.variant_row_legend", {
              index: index + 1,
            })}
          </legend>

          {/* ⚠️ ALWAYS emitted, empty for a new row — see the header. */}
          <input
            type="hidden"
            name="product[variants][][id]"
            value={variant.id ?? ""}
          />

          <div className="grid gap-1">
            <Label htmlFor={`variant-title-${index}`}>
              {t("settings.brand.products.form.variant_title_label")}
              {/* Visual marker only — `aria-required` carries the state. */}
              <span className="text-destructive ml-0.5" aria-hidden="true">
                *
              </span>
            </Label>
            {/*
              ⚠️ NO native `required` — OKS-661/OKS-692 posture, enforced by
              `bin/check-native-required`. See the longer note in
              `ProductForm.tsx`.

              It matters more here than on a single field: a native `required`
              on a repeatable row means Chrome refuses the whole submit and
              points its untranslated bubble at ONE row, so a brand with six
              variants gets an English tooltip on a row they may not even have
              scrolled to, and none of the server's localized per-row errors
              render. `Product` validates `title` on every manual variant, so
              the empty row is still refused — with a message the brand can
              read, next to the field that caused it.
            */}
            <Input
              id={`variant-title-${index}`}
              name="product[variants][][title]"
              value={variant.title}
              maxLength={255}
              aria-required="true"
              onChange={(e) => update(index, { title: e.target.value })}
            />
          </div>

          <div className="grid gap-1">
            <Label htmlFor={`variant-sku-${index}`}>
              {t("settings.brand.products.form.variant_sku_label")}
            </Label>
            <Input
              id={`variant-sku-${index}`}
              name="product[variants][][sku]"
              value={variant.sku}
              maxLength={255}
              onChange={(e) => update(index, { sku: e.target.value })}
            />
          </div>

          <div className="grid gap-1">
            <Label htmlFor={`variant-price-${index}`}>
              {t("settings.brand.products.form.variant_price_label")} (
              {currency})
            </Label>
            <Input
              id={`variant-price-${index}`}
              name="product[variants][][price]"
              inputMode="decimal"
              value={variant.price}
              onChange={(e) => update(index, { price: e.target.value })}
            />
          </div>

          <div className="flex items-center gap-2 sm:flex-col sm:items-start">
            <div className="flex items-center gap-2">
              {/*
                ⚠️ The Checkbox carries NO `name` — it is a UI control only.
                The hidden input below is the single field for this row. The
                usual hidden-"0" + named-checkbox pair would emit `available`
                twice and split the row in two. See the header.
              */}
              <Checkbox
                id={`variant-available-${index}`}
                checked={variant.available}
                onCheckedChange={(checked) =>
                  update(index, { available: checked === true })
                }
              />
              <input
                type="hidden"
                name="product[variants][][available]"
                value={variant.available ? "1" : "0"}
              />
              <Label htmlFor={`variant-available-${index}`}>
                {t("settings.brand.products.form.variant_available_label")}
              </Label>
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 sm:h-9 sm:w-9"
              aria-label={t("settings.brand.products.form.variant_remove", {
                index: index + 1,
              })}
              onClick={() => remove(index)}
            >
              <span aria-hidden="true">×</span>
            </Button>
          </div>
        </fieldset>
      ))}

      <InputError messages={toMessages(errors.variants)} />

      <div className="space-y-1">
        <Button
          id="variant-add"
          type="button"
          variant="outline"
          className="h-11 w-full sm:h-9 sm:w-auto"
          disabled={atCap}
          onClick={add}
        >
          {t("settings.brand.products.form.variant_add")}
        </Button>
        {atCap ? (
          <p className="text-muted-foreground text-xs">
            {t("settings.brand.products.form.variant_cap", { count: limit })}
          </p>
        ) : null}
      </div>
    </fieldset>
  )
}
